import {
  CheckboxOption,
  FormSection,
  Modal,
  ModalFooter,
  RadioOption,
} from '@/components/common/Modal'
import { FieldMarker } from '@/components/common/FieldMarkers'
import { resolveFieldMarkerState } from '@/components/common/fieldMarkerState'
import { useAutosave } from '@/components/common/useAutosave'
import { StatusPill } from '@/components/order/FormCard'
import type { FamilyMember, TestOptions } from '@/components/order/OrderTypes'
import type { FamilyMemberRelationship } from '@/ordering/canonicalOrder'
import {
  formatTurnaroundTimeOptionLabel,
  formatTurnaroundTimeTestTypeSectionDescription,
  getTestTypeConfig,
  getTurnaroundTimeDayBasisForTestType,
  type UiTestTypeOption,
} from '@/config/uiConfig'
import { useUiConfig } from '@/config/uiConfigHooks'
import { isSpeedSwitchBlocked, speedSwitchBlockReason } from '@/ordering/sequencingComparatorUpgrade'
import type { TestType } from '@/types/RequisitionFormTypes'
import {
  getSampleTypeLabel,
  mapCollectionStringToSampleTypeStrict,
} from '@/utils/orderFormUtils'
import {
  selectionHasRelationship,
  withRelationshipSelection,
  withRelationshipSelections,
} from '@/ordering/testOptionsRelationships'
import { hasParentData } from '@/utils/validation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type ProbandSampleType = NonNullable<TestOptions['sampleType']>

interface TestOptionsModalProps {
  isOpen: boolean
  onClose: () => void
  initialData: TestOptions
  onSave: (data: TestOptions) => void
  /**
   * P8: comparator (parent) FamilyMember rows keyed by `relationship`. The
   * modal reads MOTHER / FATHER entries to gate the "you have entered
   * information…" confirm prompts; any other relationships are ignored here.
   * Relationship-keyed (Phase B, step 6) rather than a MOTHER/FATHER-only pair
   * so the prop is not locked to the two-slot shape. Replaces the previous
   * fixed `biologicalMotherData` / `biologicalFatherData` props so callers
   * don't have to map the comparator list back to two named slots.
   */
  parentDataByRelationship?: Partial<Record<FamilyMemberRelationship, FamilyMember>>
  testTypeLabel?: string
  /** Last-sent sequencing snapshot; Discard restores this (defaults to `initialData`). */
  discardBaseline?: TestOptions
  /** When true, proband sample type is shown read-only (e.g. submitted-order comparator edits). */
  lockSampleType?: boolean
  /**
   * When true, the R5 Sequencing Speed is shown read-only (the current
   * category's speed label) instead of a radio group. Used on submitted
   * (in-progress) orders where the sequencing speed can no longer be changed —
   * only comparator-driven tier upgrades are allowed. Only affects the
   * `r5SpeedAndSampleOnly` layout.
   */
  lockSpeed?: boolean
  /**
   * When set, only these test type values are selectable (e.g. pre-accession comparator upgrades).
   * Must include the current `initialData.testType` so the modal stays valid on open.
   */
  allowedTestTypeValues?: readonly TestType[] | null
  /**
   * R5 "Sequencing Options" layout (GEN sequencing decoupling). When true the
   * modal renders a single **Sequencing Speed** radio group keyed by test-type
   * `category` (one option per category, not per tier) plus the Sample Type
   * group, and the Comparators section is removed — comparators are managed in
   * the OrderStart family section instead. Selecting a speed resolves to the
   * representative test type for that category. Opt-in so OrderInProgress (which
   * still drives per-tier comparator upgrades via `allowedTestTypeValues`) keeps
   * the legacy layout until its flow is migrated.
   */
  r5SpeedAndSampleOnly?: boolean
  /**
   * When provided, the footer renders a brand "Continue to …" CTA (label from
   * `continueLabel`) that closes this modal and advances to the next order
   * section modal. Sequencing options autosave, so continuing persists the
   * current selection just like Done.
   */
  onContinue?: () => void
  continueLabel?: string
  /**
   * GEN-4471: Current count of testing comparators (isTestingComparator !== false).
   * Used to block speed switches to categories that don't support the current count.
   */
  testingComparatorCount?: number
}

export function TestOptionsModal({
  isOpen,
  onClose,
  initialData,
  onSave,
  parentDataByRelationship,
  testTypeLabel,
  discardBaseline,
  lockSampleType = false,
  lockSpeed = false,
  allowedTestTypeValues,
  r5SpeedAndSampleOnly = false,
  onContinue,
  continueLabel = 'Continue',
  testingComparatorCount = 0,
}: TestOptionsModalProps) {
  const biologicalMotherData = parentDataByRelationship?.MOTHER
  const biologicalFatherData = parentDataByRelationship?.FATHER
  const { config } = useUiConfig()
  const [data, setData] = useState<TestOptions>(initialData)
  // Parent selection lives only in `selectedComparatorRelationships` (F.1c
  // Stage 4 / P12: relationship-first). Read each side via
  // `selectionHasRelationship`; write back via `withRelationshipSelection`
  // (single side) or `withRelationshipSelections` (bulk reset).
  const motherChecked = selectionHasRelationship(data, 'MOTHER')
  const fatherChecked = selectionHasRelationship(data, 'FATHER')

  // Snapshot pre-edit data when the modal opens so Discard reverts to that
  // baseline rather than the latest autosaved state. Prefer `discardBaseline`
  // (the last-sent sequencing snapshot for submitted-order edits) when given.
  const initialDataRef = useRef(initialData)
  initialDataRef.current = initialData
  const discardBaselineRef = useRef(discardBaseline)
  discardBaselineRef.current = discardBaseline
  const openBaselineRef = useRef<TestOptions>(initialData)

  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      openBaselineRef.current = discardBaselineRef.current ?? initialDataRef.current
      setData(initialDataRef.current)
      wasOpenRef.current = true
    }
    if (!isOpen) {
      wasOpenRef.current = false
    }
  }, [isOpen])

  const availableTestTypes = useMemo(() => {
    const all = config.testTypes
    if (allowedTestTypeValues && allowedTestTypeValues.length > 0) {
      const allow = new Set(allowedTestTypeValues)
      return all.filter((t) => allow.has(t.value))
    }
    return all
  }, [allowedTestTypeValues, config.testTypes])

  const selectedTestTypeConfig = useMemo(
    () => availableTestTypes.find((testType) => testType.value === data.testType),
    [availableTestTypes, data.testType]
  )

  /**
   * R5 "Sequencing Speed" options: one entry per test-type `category`, keyed to
   * a representative test type. The representative is the default option when
   * marked, otherwise the lowest-tier (`parentSamplesRequired`) variant so a
   * freshly-picked speed starts proband-only and comparators upgrade the tier
   * from the family section. Insertion order preserves the config's category
   * order. Only computed/rendered in the R5 layout.
   */
  const sequencingSpeedCategories = useMemo<
    ReadonlyArray<{ category: string; representative: UiTestTypeOption }>
  >(() => {
    const byCategory = new Map<string, UiTestTypeOption>()
    for (const testType of availableTestTypes) {
      const existing = byCategory.get(testType.category)
      if (!existing) {
        byCategory.set(testType.category, testType)
        continue
      }
      const isBetterRepresentative =
        testType.isDefault === true ||
        (existing.isDefault !== true && testType.parentSamplesRequired < existing.parentSamplesRequired)
      if (isBetterRepresentative) {
        byCategory.set(testType.category, testType)
      }
    }
    return [...byCategory.entries()].map(([category, representative]) => ({ category, representative }))
  }, [availableTestTypes])

  const probandSampleTypes = useMemo<ReadonlyArray<ProbandSampleType>>(() => {
    const configured = selectedTestTypeConfig?.probandSampleTypes?.filter(Boolean) ?? []
    if (configured.length === 0) {
      return selectedTestTypeConfig ? ['whole-blood'] : []
    }
    const seen = new Set<ProbandSampleType>()
    const dropped: string[] = []
    const mapped: ProbandSampleType[] = []
    for (const raw of configured) {
      const sampleType = mapCollectionStringToSampleTypeStrict(String(raw))
      if (sampleType === null) {
        dropped.push(String(raw))
        continue
      }
      if (!seen.has(sampleType)) {
        seen.add(sampleType)
        mapped.push(sampleType)
      }
    }
    if (dropped.length > 0) {
      console.warn(
        `[TestOptionsModal] Dropping ${dropped.length} unknown probandSampleTypes for testType "${selectedTestTypeConfig?.value}":`,
        dropped
      )
    }
    return mapped.length ? mapped : ['whole-blood']
  }, [selectedTestTypeConfig])

  const maxParentSamples = selectedTestTypeConfig?.parentSamplesRequired ?? 0
  const showComparatorSamples = maxParentSamples > 0
  const singleParentOnly = maxParentSamples === 1
  const requiresBothParents = maxParentSamples === 2
  /**
   * Last-sent duo identity (OrderInProgress passes `discardBaseline` = sequencing baseline).
   * Do not let users switch mother↔father on a submitted duo; they must pick trio to add the other parent.
   */
  const submittedDuoComparatorLock = useMemo((): 'mother' | 'father' | null => {
    if (!discardBaseline?.testType) {
      return null
    }
    const cfg = getTestTypeConfig(discardBaseline.testType as TestType, config)
    if (!cfg || cfg.parentSamplesRequired !== 1) {
      return null
    }
    const motherInBaseline = selectionHasRelationship(discardBaseline, 'MOTHER')
    const fatherInBaseline = selectionHasRelationship(discardBaseline, 'FATHER')
    if (motherInBaseline && !fatherInBaseline) {
      return 'mother'
    }
    if (fatherInBaseline && !motherInBaseline) {
      return 'father'
    }
    return null
  }, [config, discardBaseline])
  const duoSubmittedIdentityFrozen =
    Boolean(submittedDuoComparatorLock) &&
    singleParentOnly &&
    ((submittedDuoComparatorLock === 'mother' && motherChecked && !fatherChecked) ||
      (submittedDuoComparatorLock === 'father' && fatherChecked && !motherChecked))
  const biologicalMotherDisabled = requiresBothParents || duoSubmittedIdentityFrozen
  const biologicalFatherDisabled = requiresBothParents || duoSubmittedIdentityFrozen

  useEffect(() => {
    if (probandSampleTypes.length === 0) {
      return
    }
    // Submitted-order edits (e.g. Duo→Trio comparator add) open this modal with
    // ``lockSampleType=true``. In that mode the proband's sample type reflects
    // an already-collected physical specimen, so silently coercing it to the
    // first value allowed by a newly selected test type would (a) hide the
    // change from the user, who has no UI to opt in or out, and (b) overwrite
    // ``canonical.patient.collectionType`` on save — which the server then
    // treats as a brand-new specimen, creating duplicate ``specimen`` and
    // ``order_item`` rows for the proband. Keep the locked value as-is; the
    // upstream guardrails decide whether the test-type change is permissible.
    if (lockSampleType) {
      return
    }
    const current = data.sampleType
    const currentAllowed = current != null && probandSampleTypes.includes(current)
    if (currentAllowed) {
      return
    }
    // When the configured allowlist offers exactly one sample type, auto-select
    // it (no meaningful user choice). When multiple options are configured the
    // user must explicitly pick — mirrors the parent sample-type pattern — so
    // we clear any stale selection instead of snapping to the first option.
    if (probandSampleTypes.length === 1) {
      setData((prev) => ({ ...prev, sampleType: probandSampleTypes[0] }))
      return
    }
    if (current != null) {
      setData((prev) => ({ ...prev, sampleType: undefined }))
    }
  }, [probandSampleTypes, data.sampleType, lockSampleType])

  useEffect(() => {
    if (!showComparatorSamples && (motherChecked || fatherChecked || data.otherFamilyMember)) {
      setData((prev) =>
        withRelationshipSelections(
          { ...prev, otherFamilyMember: false },
          { MOTHER: false, FATHER: false }
        )
      )
    }
  }, [fatherChecked, motherChecked, data.otherFamilyMember, showComparatorSamples])

  /** Trio (both checked) → duo: require an explicit comparator choice instead of inheriting mother-only. */
  useEffect(() => {
    if (!singleParentOnly || !motherChecked || !fatherChecked) {
      return
    }
    setData((prev) => withRelationshipSelections(prev, { MOTHER: false, FATHER: false }))
  }, [fatherChecked, motherChecked, singleParentOnly])

  useEffect(() => {
    if (requiresBothParents && (!motherChecked || !fatherChecked)) {
      setData((prev) => withRelationshipSelections(prev, { MOTHER: true, FATHER: true }))
    }
  }, [fatherChecked, motherChecked, requiresBothParents])

  const handleSave = useCallback((value: TestOptions) => onSave(value), [onSave])

  const handleClose = () => {
    onClose()
  }

  const handleDiscard = () => {
    handleSave(openBaselineRef.current)
    onClose()
  }

  const handleContinue = () => {
    onContinue?.()
    onClose()
  }

  /** Prefer label from modal `data` so status text matches radios (parent prop can lag live canonical). */
  const selectedTestTypeLabel = selectedTestTypeConfig?.label ?? testTypeLabel ?? 'Test type selected'
  const hasSelectedTestType = Boolean(data.testType)

  const duoComparatorUnset =
    hasSelectedTestType && showComparatorSamples && singleParentOnly && !motherChecked && !fatherChecked

  /**
   * Proband sample type is required whenever the configured allowlist offers more than one
   * choice (single-option product lines auto-select via the effect above). This mirrors the
   * parent sample-type pattern: the user must pick explicitly before the modal can autosave
   * or close, so we never persist a guessed default (which would write a misleading
   * ``canonical.patient.collectionType`` and a duplicate physical specimen downstream).
   */
  const probandSampleTypeUnset =
    hasSelectedTestType && !lockSampleType && probandSampleTypes.length > 1 && data.sampleType == null

  /**
   * Suppress autosave (and Done) while the user is mid-transition into a duo without picking a comparator.
   * Otherwise the intermediate `{duo, mother:false, father:false}` snapshot would null already-persisted
   * comparator data in canonical (lost when reverting Trio → originally-submitted Duo).
   * Apply the same gate when the proband sample type is required but unset so we don't write a
   * partial ``collectionType: null`` between the test-type pick and the sample-type pick.
   */
  const { lastSaveTime, isSaving } = useAutosave(
    data,
    handleSave,
    500,
    isOpen && !duoComparatorUnset && !probandSampleTypeUnset
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Sequencing Options"
      size="lg"
      footer={
        <ModalFooter
          onDiscard={handleDiscard}
          onClose={handleClose}
          lastSaveTime={lastSaveTime}
          isSaving={isSaving}
          closeDisabled={duoComparatorUnset || probandSampleTypeUnset}
          onContinue={onContinue ? handleContinue : undefined}
          continueLabel={continueLabel}
        />
      }
    >
      <div className="flex flex-col gap-6">
        {r5SpeedAndSampleOnly ? (
          <FormSection title="Sequencing Speed" description="Estimated from date of sample receipt.">
            {lockSpeed
              ? (() => {
                  const rep =
                    sequencingSpeedCategories.find(
                      ({ category }) => category === selectedTestTypeConfig?.category
                    )?.representative ?? selectedTestTypeConfig
                  const basis = rep ? getTurnaroundTimeDayBasisForTestType(rep) : undefined
                  const daysFragment = rep
                    ? formatTurnaroundTimeOptionLabel(rep.turnaroundTimeInDays ?? 5, basis)
                    : '—'
                  return (
                    <p className="text-sm text-text-body dark:text-gray-400">
                      {selectedTestTypeConfig?.category
                        ? `${daysFragment} · ${selectedTestTypeConfig.category}`
                        : daysFragment}
                    </p>
                  )
                })()
              : sequencingSpeedCategories.map(({ category, representative }, index) => {
                  const basis = getTurnaroundTimeDayBasisForTestType(representative)
                  const daysFragment = formatTurnaroundTimeOptionLabel(
                    representative.turnaroundTimeInDays ?? 5,
                    basis
                  )
                  const blocked = isSpeedSwitchBlocked(testingComparatorCount, category, config)
                  const blockMessage = blocked ? speedSwitchBlockReason(testingComparatorCount, category, config) : null
                  return (
                    <RadioOption
                      key={category}
                      name="sequencingSpeed"
                      value={representative.value}
                      checked={selectedTestTypeConfig?.category === category}
                      onChange={(value) => setData({ ...data, testType: value as TestType })}
                      label={daysFragment}
                      description={category}
                      first={index === 0}
                      last={index === sequencingSpeedCategories.length - 1}
                      disabled={blocked}
                      helperText={blockMessage ?? undefined}
                    />
                  )
                })}
          </FormSection>
        ) : (
          <FormSection title="Test Type" description={formatTurnaroundTimeTestTypeSectionDescription()}>
            {availableTestTypes.map((testType, index) => {
              const turnaroundDays = testType.turnaroundTimeInDays ?? 5
              const basis = getTurnaroundTimeDayBasisForTestType(testType)
              const daysFragment = formatTurnaroundTimeOptionLabel(turnaroundDays, basis)
              return (
                <RadioOption
                  key={testType.value}
                  name="testType"
                  value={testType.value}
                  checked={data.testType === testType.value}
                  onChange={(value) => setData({ ...data, testType: value as TestType })}
                  label={daysFragment}
                  description={testType.label}
                  first={index === 0}
                  last={index === availableTestTypes.length - 1}
                />
              )
            })}
          </FormSection>
        )}

        {hasSelectedTestType && (
          <FormSection
            title="Patient Sample Type"
            titleMarker={
              <FieldMarker
                state={resolveFieldMarkerState(
                  probandSampleTypes.length > 1 && !lockSampleType,
                  Boolean(data.sampleType)
                )}
                fieldLabel="Patient Sample Type"
                inlineOwesSwatch
              />
            }
            description={probandSampleTypeUnset ? 'Select a sample type to continue.' : undefined}
          >
            {lockSampleType ? (
              <p className="text-sm text-text-body dark:text-gray-400">
                {data.sampleType ? getSampleTypeLabel(data.sampleType) : '—'}
              </p>
            ) : (
              probandSampleTypes.map((sampleType, index) => (
                <RadioOption
                  key={sampleType}
                  name="sampleType"
                  value={sampleType}
                  checked={data.sampleType === sampleType}
                  onChange={(value) => setData({ ...data, sampleType: value as TestOptions['sampleType'] })}
                  label={getSampleTypeLabel(sampleType)}
                  first={index === 0}
                  last={index === probandSampleTypes.length - 1}
                />
              ))
            )}
          </FormSection>
        )}

        {!r5SpeedAndSampleOnly && showComparatorSamples && (
          <FormSection
            title="Comparators"
            description={
              duoSubmittedIdentityFrozen
                ? 'This comparator was submitted with the order. Change test type to trio to add the second parent.'
                : duoComparatorUnset
                  ? 'Select Biological Mother or Biological Father. Add any time before sample is accessioned by the lab.'
                  : 'Add any time before sample is accessioned by the lab.'
            }
          >
            <div className="flex flex-col gap-3">
              <CheckboxOption
                checked={motherChecked}
                disabled={biologicalMotherDisabled}
                onChange={(checked) => {
                  if (biologicalMotherDisabled) {
                    return
                  }
                  if (singleParentOnly) {
                    if (!checked) {
                      if (
                        motherChecked &&
                        biologicalMotherData &&
                        hasParentData(biologicalMotherData)
                      ) {
                        const confirmed = window.confirm(
                          'You have entered information for Biological Mother. Unchecking this will disable testing for Biological Mother. The information you entered will be preserved but testing will not be requested. Do you want to continue?'
                        )
                        if (!confirmed) {
                          return
                        }
                      }
                      setData(withRelationshipSelections(data, { MOTHER: false, FATHER: false }))
                      return
                    }
                    setData(withRelationshipSelections(data, { MOTHER: true, FATHER: false }))
                    return
                  }
                  if (
                    !checked &&
                    motherChecked &&
                    biologicalMotherData &&
                    hasParentData(biologicalMotherData)
                  ) {
                    const confirmed = window.confirm(
                      'You have entered information for Biological Mother. Unchecking this will disable testing for Biological Mother. The information you entered will be preserved but testing will not be requested. Do you want to continue?'
                    )
                    if (!confirmed) {
                      return
                    }
                  }
                  setData(withRelationshipSelection(data, 'MOTHER', checked))
                }}
                label="Biological Mother"
              />
              <CheckboxOption
                checked={fatherChecked}
                disabled={biologicalFatherDisabled}
                onChange={(checked) => {
                  if (biologicalFatherDisabled) {
                    return
                  }
                  if (singleParentOnly) {
                    if (!checked) {
                      if (
                        fatherChecked &&
                        biologicalFatherData &&
                        hasParentData(biologicalFatherData)
                      ) {
                        const confirmed = window.confirm(
                          'You have entered information for Biological Father. Unchecking this will disable testing for Biological Father. The information you entered will be preserved but testing will not be requested. Do you want to continue?'
                        )
                        if (!confirmed) {
                          return
                        }
                      }
                      setData(withRelationshipSelections(data, { MOTHER: false, FATHER: false }))
                      return
                    }
                    setData(withRelationshipSelections(data, { MOTHER: false, FATHER: true }))
                    return
                  }
                  if (
                    !checked &&
                    fatherChecked &&
                    biologicalFatherData &&
                    hasParentData(biologicalFatherData)
                  ) {
                    const confirmed = window.confirm(
                      'You have entered information for Biological Father. Unchecking this will disable testing for Biological Father. The information you entered will be preserved but testing will not be requested. Do you want to continue?'
                    )
                    if (!confirmed) {
                      return
                    }
                  }
                  setData(withRelationshipSelection(data, 'FATHER', checked))
                }}
                label="Biological Father"
              />
            </div>
          </FormSection>
        )}

        {r5SpeedAndSampleOnly ? (
          hasSelectedTestType ? (
            <StatusPill
              text={
                probandSampleTypeUnset
                  ? `${selectedTestTypeConfig?.category ?? selectedTestTypeLabel} selected — choose a sample type`
                  : `${selectedTestTypeConfig?.category ?? selectedTestTypeLabel} selected`
              }
              variant={probandSampleTypeUnset ? 'info' : 'success'}
            />
          ) : (
            <StatusPill text="Choose a sequencing speed and sample type to place this order" variant="info" />
          )
        ) : hasSelectedTestType ? (
          <StatusPill
            text={
              probandSampleTypeUnset
                ? `${selectedTestTypeLabel} selected — choose a sample type`
                : duoComparatorUnset
                  ? `${selectedTestTypeLabel} selected — choose a comparator (mother or father)`
                  : `${selectedTestTypeLabel} selected`
            }
            variant={probandSampleTypeUnset || duoComparatorUnset ? 'info' : 'success'}
          />
        ) : (
          <StatusPill text="Choose sequencing timing, sample type, and comparators to place this order" variant="info" />
        )}
      </div>
    </Modal>
  )
}
